import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LogIn, TriangleAlert } from 'lucide-react';
import { api, type AuthState } from '../lib/api';
import { Spinner } from './ui';

/**
 * Mur de connexion (AUTH-03). Quand l'OIDC est configuré, l'app n'est rendue
 * qu'aux utilisateurs connectés. Tant qu'il ne l'est pas, accès ouvert : on rend
 * directement le contenu (aucun changement pour le dev / le prototype).
 */
export function Gate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);

  useEffect(() => {
    void api.auth
      .me()
      .then(setState)
      .catch(() => setState({ user: null, oidcConfigured: false }));
  }, []);

  if (!state) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner />
      </div>
    );
  }
  if (state.oidcConfigured && !state.user) return <LoginWall />;
  return <>{children}</>;
}

function LoginWall() {
  const { t } = useTranslation();
  const authError = new URLSearchParams(location.search).get('auth');
  return (
    <div className="grid h-full place-items-center px-4">
      <div className="flex max-w-sm flex-col items-center gap-5 text-center">
        <img src="/icon.svg" alt="" aria-hidden="true" className="h-14 w-14" />
        <div>
          <h1 className="text-xl font-semibold">{t('auth.wallTitle')}</h1>
          <p className="mt-1 text-sm text-muted">{t('auth.wallSubtitle')}</p>
        </div>
        {authError && (
          <p className="flex items-center gap-1.5 rounded-lg bg-amber-500/10 px-3 py-1.5 text-sm text-amber-600 dark:text-amber-400">
            <TriangleAlert className="h-4 w-4" /> {t('auth.failed')}
          </p>
        )}
        <a
          href={api.auth.loginUrl}
          onClick={() => {
            // Garde la cible d'un lien partagé pour y revenir après connexion.
            try {
              if (location.hash.startsWith('#/')) localStorage.setItem('gnosis.returnHash', location.hash);
            } catch {
              /* stockage indisponible */
            }
          }}
          className="flex items-center gap-2 rounded-xl bg-accent px-5 py-2.5 font-medium text-accent-fg hover:opacity-90"
        >
          <LogIn className="h-4 w-4" /> {t('auth.loginMs')}
        </a>
      </div>
    </div>
  );
}
